/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Row,
    Col,
    Card,
    Divider,
    Button,
    Space,
    Image,
    Checkbox,
    Select,
} from 'antd';
import {
    BedIcon,
    ExtraBedIcon,
    RoomSizeIcon,
    TickIcon,
    CloseIcon,
    ChildIcon,
    UserIcon,
} from '@/assets/images/icons/screens';
import BlockDescription from '@/components/Information/WithImage/Block';
import { actions } from '@/redux/invoice/create/reducer';
// import { dateFormat } from '@/helpers/dateFormat';
import ExtraBeds from './ExtraBed';

const getRoomDetails = (room) => {
    const roomName = room.room_name;

    const count = room.prices.reduce((sum, p) => sum + p.no_rooms, 0);

    const roomTotal = room.prices.reduce((sum, p) => sum + p.no_rooms * p.price, 0);

    const extraServicesTotal = room.prices.reduce((sum, p) => {
        const servicesSum = (p.extra_services || []).reduce(
            (sSum, s) => sSum + s.price * p.no_rooms,
            0,
        );
        return sum + servicesSum;
    }, 0);

    const extraBedsCount = room.prices.reduce((sum, p) => sum + (p.extra_beds || 0), 0);

    const extraBedsPrice = room.prices.reduce((sum, p) => {
        const beds = p.extra_beds || 0;
        const bedPrice = p.extra_bed_charges_type === 'Decimal'
            ? parseFloat(p.extra_beds_price) * beds
            : ((p.price * parseFloat(p.extra_beds_price)) / 100) * beds;
        return sum + bedPrice;
    }, 0);

    const includedServices = room.prices.reduce((acc, p) => {
        (p.extra_services || []).forEach((service) => {
            const key = service.id || service.name;
            if (!acc[key]) {
                acc[key] = { ...service, totalRooms: 0 };
            }
            acc[key].totalRooms += p.no_rooms;
        });
        return acc;
    }, {});

    const hasExtraServices = Object.keys(includedServices).length > 0;
    const hasExtraBeds = extraBedsCount > 0;

    return {
        roomName,
        count,
        roomTotal,
        extraServicesTotal,
        extraBedsCount,
        extraBedsPrice,
        includedServices,
        hasExtraServices,
        hasExtraBeds,
    };
};

const ReservationList = ({
    accommodationId,
    data,
    filterData,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { replace } = useHistory();

    const [visible, setVisible] = useState(false);
    const [selectedRoomList, setSelectedRoomList] = useState(JSON.parse(localStorage.getItem('selectedRoomList')) || []);
    const [selectedExtraServices, setSelectedExtraServices] = useState(JSON.parse(localStorage.getItem('selectedExtraServices')) || {});
    const [extraBedsNo, setExtraBedsNo] = useState(JSON.parse(localStorage.getItem('extraBedsNo')) || {});
    const [selectedRoom, setSelectedRoom] = useState(JSON.parse(localStorage.getItem('selectedRoom')) || {});

    const handleServiceCheckboxChange = (checkedValues, item, price) => {
        setSelectedRoomList((prev) => {
            const updated = [...prev];
            const roomIndex = updated.findIndex((r) => r.room_id === item.id);

            const selectedServices = item.extra_services.included_with_price
                .filter((service) => checkedValues.includes(service.id))
                .map((service) => ({
                    id: service.id,
                    name: `${service.name} ${item.currency_icon}${service.price}`,
                    price: service.price,
                }));

            if (roomIndex === -1) {
                updated.push({
                    room_id: item.id,
                    room_name: item.type_name,
                    prices: [{
                        id: price.id,
                        price: price.price,
                        price_type: price.price_type,
                        no_rooms: 0,
                        extra_beds: 0,
                        extra_beds_price: item.extra_bed_charge,
                        extra_bed_charges_type: item.extra_bed_charges_type,
                        extra_services: selectedServices,
                    }],
                });
            } else {
                const currentRoom = { ...updated[roomIndex] };
                const prices = [...currentRoom.prices];
                const priceIndex = prices.findIndex((p) => p.id === price.id);

                if (priceIndex === -1) {
                    prices.push({
                        id: price.id,
                        price: price.price,
                        price_type: price.price_type,
                        no_rooms: 0,
                        extra_beds: 0,
                        extra_beds_price: item.extra_bed_charge,
                        extra_bed_charges_type: item.extra_bed_charges_type,
                        extra_services: selectedServices,
                    });
                } else {
                    prices[priceIndex] = {
                        ...prices[priceIndex],
                        extra_services: selectedServices,
                    };
                }

                updated[roomIndex] = {
                    ...currentRoom,
                    prices,
                };
            }

            return updated;
        });
    };

    const handleExtraBedsChange = (extraBedCount, item, price) => {
        setSelectedRoomList((prev) => {
            const updated = [...prev];
            const roomIndex = updated.findIndex((r) => r.room_id === item.id);

            const priceEntry = {
                id: price.id,
                price: price.price,
                price_type: price.price_type,
                no_rooms: 0,
                extra_beds: extraBedCount,
                extra_beds_price: item.extra_bed_charge,
                extra_bed_charges_type: item.extra_bed_charges_type,
                extra_services: [],
            };

            if (roomIndex === -1) {
                updated.push({
                    room_id: item.id,
                    room_name: item.type_name,
                    prices: [priceEntry],
                });
            } else {
                const room = { ...updated[roomIndex] };
                const prices = [...room.prices];
                const priceIndex = prices.findIndex((p) => p.id === price.id);

                if (priceIndex === -1) {
                    prices.push(priceEntry);
                } else {
                    prices[priceIndex] = {
                        ...prices[priceIndex],
                        extra_beds: extraBedCount,
                        extra_beds_price: item.extra_bed_charge,
                        extra_bed_charges_type: item.extra_bed_charges_type,
                    };
                }

                updated[roomIndex] = { ...room, prices };
            }

            return updated;
        });
    };

    const handleRoomSelectChange = (value, item, price) => {
        setSelectedRoomList((prev) => {
            const updated = [...prev];
            const roomIndex = updated.findIndex((r) => r.room_id === item.id);

            if (value === null || value === undefined) {
                if (roomIndex !== -1) {
                    updated[roomIndex] = {
                        ...updated[roomIndex],
                        prices: updated[roomIndex].prices.filter((p) => p.id !== price.id),
                    };
                }
                return updated;
            }

            const priceEntry = {
                id: price.id,
                price: price.price,
                price_type: price.price_type,
                no_rooms: value,
                extra_beds: 0,
                extra_beds_price: item.extra_bed_charge,
                extra_bed_charges_type: item.extra_bed_charges_type,
                extra_services: [],
            };

            if (roomIndex === -1) {
                updated.push({
                    room_id: item.id,
                    room_name: item.type_name,
                    prices: [priceEntry],
                });
            } else {
                const priceIndex = updated[roomIndex].prices.findIndex(
                    (p) => p.id === price.id,
                );

                const updatedPrices = [...updated[roomIndex].prices];
                if (priceIndex === -1) {
                    updatedPrices.push(priceEntry);
                } else {
                    updatedPrices[priceIndex] = {
                        ...updatedPrices[priceIndex],
                        no_rooms: value,
                    };
                }

                updated[roomIndex] = {
                    ...updated[roomIndex],
                    prices: updatedPrices,
                };
            }

            return updated;
        });
    };

    const makeReservation = () => {
        const reservationData = {
            reservation_type: 'Hotel',
            tour_id: accommodationId,
            check_in: filterData.date_start,
            check_out: filterData.date_end,
            adults: filterData.adults,
            children: filterData.children,
            ages: filterData.ages,
            currency_icon: data[0]?.currency_icon,
            data: selectedRoomList,
        };
        dispatch(actions.prepareReservationDataForSubmit(reservationData));
        dispatch(actions.requestGetReservationData(
            accommodationId,
            {
                type: 0,
                check_in: filterData.date_start,
                check_out: filterData.date_end,
                data: selectedRoomList,
            },
        ));
        replace('/app/invoices/new');
    };

    useEffect(() => {
        localStorage.setItem('extraBedsNo', JSON.stringify(extraBedsNo));
        localStorage.setItem('selectedExtraServices', JSON.stringify(selectedExtraServices));
        localStorage.setItem('selectedRoomList', JSON.stringify(selectedRoomList));
        localStorage.setItem('selectedRoom', JSON.stringify(selectedRoom));
    }, [extraBedsNo, selectedExtraServices, selectedRoom, selectedRoomList]);

    const filteredRooms = selectedRoomList.filter((room) => room.prices.reduce((sum, p) => sum + p.no_rooms, 0) !== 0);

    // console.log(selectedRoomList);
    return (
        <Row className="hotel-reservation-list" gutter={20}>
            <Col lg={20} xs={24} className="left-content">
                <div className="table-scroll-wrapper">
                    <div className="table-header">
                        <Row>
                            <Col span={6}><span>{t('Room Type')}</span></Col>
                            <Col span={6}><span>{`${t('Services')} & ${t('Room Policy')}`}</span></Col>
                            <Col span={5}><span>{t('Number of Guests')}</span></Col>
                            <Col span={4}><span>{`${t('Prices')}`}</span></Col>
                            <Col span={3}><span>{t('Select Rooms')}</span></Col>
                        </Row>
                    </div>
                    <div className="table-content">
                        {data.map((item) => (
                            <div key={item.id}>
                                <Row>
                                    <Col span={6} className="room-details">
                                        <Space direction="vertical">
                                            <div className="room-image">
                                                <Image
                                                    preview={{
                                                        visible: false,
                                                        mask: (
                                                            <div className="image-mask">
                                                                {t('View Room').toUpperCase()}
                                                            </div>
                                                        ),
                                                    }}
                                                    src={item.images[0]?.url}
                                                    onClick={() => setVisible(true)}
                                                />
                                                <div style={{ display: 'none' }}>
                                                    <Image.PreviewGroup
                                                        preview={{
                                                            visible,
                                                            onVisibleChange: (vis) => setVisible(vis),
                                                        }}
                                                    >
                                                        {item.images.map((image) => (
                                                            <Image
                                                                key={image.id}
                                                                src={image.url}
                                                                alt={image.url}
                                                            />
                                                        ))}
                                                    </Image.PreviewGroup>
                                                </div>
                                            </div>
                                            <div className="room-details-content">
                                                <div className="room-type-name">
                                                    {item.type_name}
                                                </div>
                                                <Space direction="vertical">
                                                    {item.room_size && (
                                                        <BlockDescription
                                                            icon={<RoomSizeIcon />}
                                                            label={`${t('Room Size')}: ${item.room_size} m²`}
                                                        />
                                                    )}
                                                    { item.bed_type_for_adult && (
                                                        <BlockDescription
                                                            icon={<BedIcon />}
                                                            label={`${item.bed_type_for_adult}: ${item.adult_beds_no}`}
                                                        />
                                                    )}
                                                    {item.extra_beds && (
                                                        <BlockDescription
                                                            icon={<ExtraBedIcon />}
                                                            label={`${t('Extra Bed')}: ${item.extra_beds}`}
                                                            content={`${t('Extra Charges')}: ${item.extra_bed_charge}${item.extra_bed_charges_type === 'Decimal' ? item.currency_icon : '%'}`}
                                                        />
                                                    )}
                                                </Space>
                                            </div>
                                        </Space>
                                        <Divider dashed />
                                        <div className="amenities-wrapper">
                                            <div className="amenities-title">{t('Amenities')}</div>
                                            {item.amenities.map((amenity) => (
                                                <div key={amenity.id}>
                                                    <Space align="start" size={12}>
                                                        <TickIcon />
                                                        <span>{amenity.name}</span>
                                                    </Space>
                                                </div>
                                            ))}
                                        </div>
                                    </Col>
                                    <Col span={6} className="extra-services-room-policy">
                                        <div className="extra-services">
                                            <Space direction="vertical" className="included-service">
                                                {item.extra_services?.included?.map((service) => (
                                                    <div key={service.id}>
                                                        <Space align="start" size={12}>
                                                            <TickIcon />
                                                            <span>{service.name}</span>
                                                        </Space>
                                                    </div>
                                                ))}
                                            </Space>
                                            <Space direction="vertical" className="excluded-service">
                                                {item.extra_services?.excluded?.map((service) => (
                                                    <div key={service.id}>
                                                        <Space align="start" size={12}>
                                                            <CloseIcon />
                                                            <span>{service.name}</span>
                                                        </Space>
                                                    </div>
                                                ))}
                                            </Space>
                                        </div>
                                        <Divider dashed />
                                        <div className="room-policy">
                                            <div className="room-policy-title">{t('Room Policy')}</div>
                                            <Space direction="vertical">
                                                {item.room_policy?.map((policy) => (
                                                    <div key={policy.id}>
                                                        <Space align="start" size={12}>
                                                            <TickIcon />
                                                            <span>{policy.name}</span>
                                                        </Space>
                                                    </div>
                                                ))}
                                            </Space>
                                        </div>
                                    </Col>
                                    <Col span={12} className="prices">
                                        {item.prices.map((price) => (
                                            <Col key={price.id}>
                                                <Row>
                                                    <Col span={10} className="number-of-guests">
                                                        <Space direction="vertical">
                                                            {Number(price.max_adults) > 0 && (
                                                                <BlockDescription
                                                                    icon={<UserIcon />}
                                                                    label={(
                                                                        <>
                                                                            <span>
                                                                                {t('Max. Adults')}
                                                                                :
                                                                            </span>
                                                                            <span className="value">{price.max_adults}</span>
                                                                        </>
                                                                    )}
                                                                    content={
                                                                        price.adult_beds_no > 0 && price.bed_type_for_adult
                                                                            ? `${price.adult_beds_no} ${price.bed_type_for_adult}`
                                                                            : ''
                                                                    }
                                                                />
                                                            )}
                                                            {Number(price.max_child) > 0 && (
                                                                <BlockDescription
                                                                    icon={<ChildIcon />}
                                                                    label={(
                                                                        <>
                                                                            <span>
                                                                                {t('Max. Children')}
                                                                                :
                                                                            </span>
                                                                            <span className="value">{price.max_child}</span>
                                                                        </>
                                                                    )}
                                                                    content={
                                                                        price.child_beds_no > 0 && price.bed_type_for_child
                                                                            ? `${price.child_beds_no} ${price.bed_type_for_child}`
                                                                            : ''
                                                                    }
                                                                />
                                                            )}
                                                        </Space>
                                                    </Col>
                                                    <Col span={8} className="price">
                                                        <div className="price-value">
                                                            {`${item.currency_icon}${price.price} / ${t('Night')} ${price.price_type}`}
                                                        </div>
                                                        <div className="extra-services-title">{t('Extra Services')}</div>
                                                        <Checkbox.Group
                                                            value={selectedExtraServices[`${item.id}-${price.id}`] || []}
                                                            onChange={(checkedValues) => {
                                                                setSelectedExtraServices((prev) => ({
                                                                    ...prev,
                                                                    [`${item.id}-${price.id}`]: checkedValues,
                                                                }));
                                                                handleServiceCheckboxChange(checkedValues, item, price);
                                                            }}
                                                        >
                                                            {item.extra_services?.included_with_price?.map((s) => (
                                                                <div key={s.id}>
                                                                    <Space align="start" size={12}>
                                                                        <Checkbox value={s.id}>
                                                                            <span className="service-name">{s.name}</span>
                                                                            <span>{item.currency_icon}</span>
                                                                            <span>{s.price}</span>
                                                                        </Checkbox>
                                                                    </Space>
                                                                </div>
                                                            ))}
                                                        </Checkbox.Group>
                                                        <ExtraBeds
                                                            extraBedsTotal={item.extra_beds}
                                                            value={extraBedsNo[`${item.id}-${price.id}`] || 0}
                                                            onChange={(count) => {
                                                                setExtraBedsNo((prev) => ({
                                                                    ...prev,
                                                                    [`${item.id}-${price.id}`]: count,
                                                                }));
                                                                handleExtraBedsChange(count, item, price);
                                                            }}
                                                        />
                                                        {/* <Space>
                                                            {price.offer.map((offer) => (
                                                                <div key={offer.id} className={`offers ${offer.type}`}>
                                                                    {offer.name}
                                                                </div>
                                                            ))}
                                                        </Space> */}
                                                        {/* <Space direction="vertical" className="offers-wrapper">
                                                            <div className="offers discount">
                                                                20% off
                                                            </div>
                                                            <div className="offers last_minute">
                                                                Last Minute
                                                            </div>
                                                            <div className="offers early_booking">
                                                                Early Booking
                                                            </div>
                                                            <div className="offers free_night">
                                                                Free Night
                                                            </div>
                                                        </Space> */}
                                                    </Col>
                                                    <Col span={6} className="select-rooms">
                                                        <Select
                                                            allowClear
                                                            value={selectedRoom[`${item.id}-${price.id}`] || null}
                                                            onChange={(value) => {
                                                                if (value === null || value === undefined) {
                                                                    setSelectedExtraServices((prev) => {
                                                                        const updated = { ...prev };
                                                                        delete updated[`${item.id}-${price.id}`];
                                                                        return updated;
                                                                    });

                                                                    setExtraBedsNo((prev) => {
                                                                        const updated = { ...prev };
                                                                        delete updated[`${item.id}-${price.id}`];
                                                                        return updated;
                                                                    });
                                                                }
                                                                setSelectedRoom((prev) => ({
                                                                    ...prev,
                                                                    [`${item.id}-${price.id}`]: value,
                                                                }));
                                                                handleRoomSelectChange(value, item, price);
                                                            }}
                                                        >
                                                            {new Array(item.no_rooms).fill(0).map((_, i) => (
                                                                <Select.Option
                                                                    key={i.toString()}
                                                                    value={i + 1}
                                                                >
                                                                    {i + 1}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        ))}
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                </div>
            </Col>
            {filteredRooms.length > 0 && (
                <Col lg={4} xs={24} className="right-content">
                    <Card
                        title="Reservation"
                        bordered={false}
                    >
                        <div className="rooms">
                            {filteredRooms.map((room) => {
                                const {
                                    roomName,
                                    count,
                                    roomTotal,
                                    extraServicesTotal,
                                    extraBedsCount,
                                    extraBedsPrice,
                                    includedServices,
                                    hasExtraServices,
                                    hasExtraBeds,
                                } = getRoomDetails(room);
                                return (
                                    <div key={room.room_id}>
                                        <span className="total-rooms">{count}</span>
                                        <span className="desc">{`${roomName} ${t('selected')}`}</span>
                                        {hasExtraServices && (
                                            <>
                                                <div className="included">{t('Included')}</div>
                                                <Row gutter={[0, 10]} className="included-list">
                                                    {Object.entries(includedServices).map(([key, service]) => (
                                                        <Col key={key}>
                                                            <Space align="start">
                                                                <TickIcon />
                                                                <span>{`${service.name} × ${service.totalRooms}`}</span>
                                                            </Space>
                                                        </Col>
                                                    ))}
                                                </Row>

                                                {hasExtraBeds && (
                                                    <>
                                                        <div className="included">{t('Extra Beds')}</div>
                                                        <div className="included-list">
                                                            <span>{extraBedsCount}</span>
                                                            {' '}
                                                            <span>{extraBedsCount === 1 ? t('Extra Bed') : t('Extra Beds')}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <div className="total-price">
                                            {extraServicesTotal !== 0 || extraBedsPrice !== 0 ? (
                                                `${t('Total Price')} ${roomTotal} + ${extraServicesTotal} + ${extraBedsPrice.toFixed(2)}`
                                            ) : (
                                                `${t('Total Price')} ${roomTotal}`
                                            )}
                                        </div>
                                        <Divider dashed />
                                    </div>
                                );
                            })}
                        </div>
                        <Divider />
                        <div className="card-btn-wrapper">
                            <Button
                                onClick={() => makeReservation()}
                            >
                                {t('Reserve')}
                            </Button>
                        </div>
                    </Card>
                </Col>
            )}
        </Row>
    );
};

export default ReservationList;
